/**
 * Compare Page - Side-by-side algorithm comparison
 */
import React, { useState, useEffect } from 'react';
import { algorithmService } from '../services/api';
import './ComparePage.css';

function CompareSection({ label, left, right, mono = false }) {
  return (
    <div className="compare-section">
      <h4 className="compare-section-label">{label}</h4>
      <div className="compare-row">
        <div className={`compare-cell ${mono ? 'mono' : ''}`}>
          {left || <span className="text-muted">—</span>}
        </div>
        <div className={`compare-cell ${mono ? 'mono' : ''}`}>
          {right || <span className="text-muted">—</span>}
        </div>
      </div>
    </div>
  );
}

function ComparePage() {
  const [algorithms, setAlgorithms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leftId, setLeftId] = useState('');
  const [rightId, setRightId] = useState('');
  const [leftAlgo, setLeftAlgo] = useState(null);
  const [rightAlgo, setRightAlgo] = useState(null);
  const [detailLoading, setDetailLoading] = useState({ left: false, right: false });

  // Load all algorithms for the dropdowns
  useEffect(() => {
    algorithmService.getAll()
      .then(res => setAlgorithms(res.data || []))
      .finally(() => setLoading(false));
  }, []);

  // Load left algorithm detail
  useEffect(() => {
    if (!leftId) { setLeftAlgo(null); return; }
    setDetailLoading(prev => ({ ...prev, left: true }));
    algorithmService.getById(leftId)
      .then(res => setLeftAlgo(res.data))
      .finally(() => setDetailLoading(prev => ({ ...prev, left: false })));
  }, [leftId]);

  // Load right algorithm detail
  useEffect(() => {
    if (!rightId) { setRightAlgo(null); return; }
    setDetailLoading(prev => ({ ...prev, right: true }));
    algorithmService.getById(rightId)
      .then(res => setRightAlgo(res.data))
      .finally(() => setDetailLoading(prev => ({ ...prev, right: false })));
  }, [rightId]);

  const complexityColor = (val) => {
    if (!val) return '';
    if (val.includes('O(1)')) return 'complexity-green';
    if (val.includes('log')) return 'complexity-green';
    if (val.includes('n²') || val.includes('n^2')) return 'complexity-red';
    return 'complexity-yellow';
  };

  return (
    <div className="compare-page">
      <div className="container">
        <div className="page-header">
          <h1>Compare Algorithms</h1>
          <p>Select two algorithms to compare side-by-side</p>
        </div>

        {/* Selectors */}
        <div className="compare-selectors">
          <div className="selector-col">
            <label>Algorithm A</label>
            <select
              className="filter-select w-full"
              value={leftId}
              onChange={e => setLeftId(e.target.value)}
              disabled={loading}
            >
              <option value="">Select an algorithm...</option>
              {algorithms.map(a => (
                <option key={a.id} value={a.id} disabled={a.id === rightId}>
                  {a.name} ({a.unit})
                </option>
              ))}
            </select>
          </div>

          <div className="vs-badge">VS</div>

          <div className="selector-col">
            <label>Algorithm B</label>
            <select
              className="filter-select w-full"
              value={rightId}
              onChange={e => setRightId(e.target.value)}
              disabled={loading}
            >
              <option value="">Select an algorithm...</option>
              {algorithms.map(a => (
                <option key={a.id} value={a.id} disabled={a.id === leftId}>
                  {a.name} ({a.unit})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Comparison Table */}
        {(leftAlgo || rightAlgo) && (
          <div className="compare-table">
            {/* Headers */}
            <div className="compare-headers">
              <div className="compare-header-label" />
              <div className="compare-header">
                {detailLoading.left ? (
                  <div className="spinner" style={{ width: 24, height: 24 }} />
                ) : leftAlgo ? (
                  <>
                    <h3>{leftAlgo.name}</h3>
                    <span className={`badge badge-${leftAlgo.difficulty?.toLowerCase()}`}>
                      {leftAlgo.difficulty}
                    </span>
                  </>
                ) : <span className="text-muted">Not selected</span>}
              </div>
              <div className="compare-header">
                {detailLoading.right ? (
                  <div className="spinner" style={{ width: 24, height: 24 }} />
                ) : rightAlgo ? (
                  <>
                    <h3>{rightAlgo.name}</h3>
                    <span className={`badge badge-${rightAlgo.difficulty?.toLowerCase()}`}>
                      {rightAlgo.difficulty}
                    </span>
                  </>
                ) : <span className="text-muted">Not selected</span>}
              </div>
            </div>

            {/* Rows */}
            <CompareSection
              label="Full Name"
              left={leftAlgo?.fullName}
              right={rightAlgo?.fullName}
            />
            <CompareSection
              label="Category"
              left={leftAlgo?.category}
              right={rightAlgo?.category}
            />
            <CompareSection
              label="Unit"
              left={leftAlgo?.unit}
              right={rightAlgo?.unit}
            />

            {/* Complexity */}
            <div className="compare-section">
              <h4 className="compare-section-label">Time (Best)</h4>
              <div className="compare-row">
                <div className={`compare-cell mono ${complexityColor(leftAlgo?.timeComplexity?.best)}`}>
                  {leftAlgo?.timeComplexity?.best || '—'}
                </div>
                <div className={`compare-cell mono ${complexityColor(rightAlgo?.timeComplexity?.best)}`}>
                  {rightAlgo?.timeComplexity?.best || '—'}
                </div>
              </div>
            </div>

            <div className="compare-section">
              <h4 className="compare-section-label">Time (Average)</h4>
              <div className="compare-row">
                <div className={`compare-cell mono ${complexityColor(leftAlgo?.timeComplexity?.average)}`}>
                  {leftAlgo?.timeComplexity?.average || '—'}
                </div>
                <div className={`compare-cell mono ${complexityColor(rightAlgo?.timeComplexity?.average)}`}>
                  {rightAlgo?.timeComplexity?.average || '—'}
                </div>
              </div>
            </div>

            <div className="compare-section">
              <h4 className="compare-section-label">Time (Worst)</h4>
              <div className="compare-row">
                <div className={`compare-cell mono ${complexityColor(leftAlgo?.timeComplexity?.worst)}`}>
                  {leftAlgo?.timeComplexity?.worst || '—'}
                </div>
                <div className={`compare-cell mono ${complexityColor(rightAlgo?.timeComplexity?.worst)}`}>
                  {rightAlgo?.timeComplexity?.worst || '—'}
                </div>
              </div>
            </div>

            <div className="compare-section">
              <h4 className="compare-section-label">Space Complexity</h4>
              <div className="compare-row">
                <div className={`compare-cell mono ${complexityColor(leftAlgo?.spaceComplexity)}`}>
                  {leftAlgo?.spaceComplexity || '—'}
                </div>
                <div className={`compare-cell mono ${complexityColor(rightAlgo?.spaceComplexity)}`}>
                  {rightAlgo?.spaceComplexity || '—'}
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="compare-section">
              <h4 className="compare-section-label">Description</h4>
              <div className="compare-row">
                <div className="compare-cell">
                  <p>{leftAlgo?.description || '—'}</p>
                </div>
                <div className="compare-cell">
                  <p>{rightAlgo?.description || '—'}</p>
                </div>
              </div>
            </div>

            {/* Advantages */}
            <div className="compare-section">
              <h4 className="compare-section-label">Advantages</h4>
              <div className="compare-row">
                <div className="compare-cell">
                  {leftAlgo?.advantages?.map((a, i) => (
                    <div key={i} className="compare-list-item text-green">+ {a}</div>
                  )) || '—'}
                </div>
                <div className="compare-cell">
                  {rightAlgo?.advantages?.map((a, i) => (
                    <div key={i} className="compare-list-item text-green">+ {a}</div>
                  )) || '—'}
                </div>
              </div>
            </div>

            {/* Disadvantages */}
            <div className="compare-section">
              <h4 className="compare-section-label">Disadvantages</h4>
              <div className="compare-row">
                <div className="compare-cell">
                  {leftAlgo?.disadvantages?.map((d, i) => (
                    <div key={i} className="compare-list-item text-red">− {d}</div>
                  )) || '—'}
                </div>
                <div className="compare-cell">
                  {rightAlgo?.disadvantages?.map((d, i) => (
                    <div key={i} className="compare-list-item text-red">− {d}</div>
                  )) || '—'}
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="compare-section">
              <h4 className="compare-section-label">Tags</h4>
              <div className="compare-row">
                <div className="compare-cell">
                  <div className="compare-tags">
                    {leftAlgo?.tags?.map(t => <span key={t} className="tag">#{t}</span>) || '—'}
                  </div>
                </div>
                <div className="compare-cell">
                  <div className="compare-tags">
                    {rightAlgo?.tags?.map(t => <span key={t} className="tag">#{t}</span>) || '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty prompt */}
        {!leftAlgo && !rightAlgo && !loading && (
          <div className="compare-empty card">
            <div className="compare-empty-icon">⚖️</div>
            <h3>Select Two Algorithms</h3>
            <p>Choose algorithms from the dropdowns above to see a detailed side-by-side comparison of complexity, advantages, disadvantages, and more.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ComparePage;
